/**
 * Peitho waitlist client (T11 §3.4) — public, lite-session aware.
 *
 * This is the user-facing surface of Peitho waitlist. It is NOT the admin
 * surface (`src/modules/peitho/api.ts`) — that one attaches a Cerberus
 * Bearer JWT and hits admin-only endpoints. Lite session users have no
 * JWT; we instead carry the token via the `cuidari_waitlist_token` cookie
 * (`credentials: 'include'`) or, for the magic-link flow, as a `?t=`
 * query param accepted by Peitho.
 *
 * Endpoints used:
 *   - `POST /v1/waitlist`                 — signup; returns `lite_session_token`
 *   - `GET  /v1/waitlist/me`              — read position + invites + referral_link
 *   - `POST /v1/waitlist/resend_magic_link` — re-emit existing token via Iris (rate-limited)
 *
 * Errors come back as `WaitlistApiError`, mirroring the pattern used by
 * the admin client + the eunomia-review module.
 */

import { ensurePeithoOk, peithoBaseUrl, unwrapData } from '@/lib/http/peitho'
import { liteSession } from './lite-session'

export class WaitlistApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string | null,
    public body: unknown,
  ) {
    super(message)
    this.name = 'WaitlistApiError'
  }
}

export interface WaitlistMe {
  position: number
  ahead: number
  behind: number
  invited_count: number
  invited_activated: number
  next_milestone: number | null
  referral_link: string
  status: 'waiting' | 'invited' | 'converted'
}

interface WaitlistSignupResponse {
  lite_session_token: string
  entry_id: string
  position: number
  email: string
}

async function ensureOk(res: Response, label: string): Promise<unknown> {
  return ensurePeithoOk(res, `Peitho ${label}`, WaitlistApiError)
}

/**
 * POST /v1/waitlist — signs the email up for the waitlist. On success
 * Peitho returns the `lite_session_token`; we persist it via
 * `liteSession.setToken()` so the next page load can call `getMe()`
 * without re-asking for the email.
 */
export async function signup(
  opts: { email: string; domain_slug?: string; signal?: AbortSignal },
): Promise<WaitlistSignupResponse> {
  const root = await peithoBaseUrl()
  const res = await fetch(`${root}/v1/waitlist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      email: opts.email,
      domain_slug: opts.domain_slug ?? 'cuidari',
    }),
    signal: opts.signal,
  })
  const body = await ensureOk(res, 'POST /v1/waitlist')
  const data = unwrapData<WaitlistSignupResponse>(body)
  if (data?.lite_session_token) {
    liteSession.setToken(data.lite_session_token)
  }
  return data
}

/**
 * GET /v1/waitlist/me — reads the user's waitlist state. Token travels
 * via the `cuidari_waitlist_token` cookie (Peitho also accepts
 * `?t=<token>` for the magic-link first-hit). Returns null when no
 * token is set or the token has been revoked.
 */
export async function getMe(opts?: {
  tokenOverride?: string | null
  signal?: AbortSignal
}): Promise<WaitlistMe | null> {
  const root = await peithoBaseUrl()
  const token = opts?.tokenOverride ?? liteSession.getToken()
  if (!token) return null
  const params = new URLSearchParams({ t: token })
  const res = await fetch(`${root}/v1/waitlist/me?${params.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'include',
    signal: opts?.signal,
  })
  if (res.status === 401 || res.status === 404) {
    // Token revoked or unknown — surface as null so the caller can fall
    // back to the form. We do not auto-clear the cookie because a 401
    // could be transient (token rotation mid-flight); UX layer decides.
    return null
  }
  const body = await ensureOk(res, 'GET /v1/waitlist/me')
  return unwrapData<WaitlistMe>(body)
}
